/**
 * Legal document renderer
 *
 * Renders a structured legal document (Terms or Privacy) from src/content/legal.ts.
 * Public — usable while signed out. Includes a language toggle (ES/EN), a back
 * link, and cross-links between the two legal documents.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Map, ArrowLeft } from 'lucide-react';
import {
    COMPANY_NAME,
    OPERATOR_NAME,
    OPERATOR_RUC,
    OPERATOR_COUNTRY,
    LEGAL_EFFECTIVE_DATE,
    termsContent,
    privacyContent,
    type Lang,
    type LegalDoc,
} from '../../content/legal';

interface LegalDocumentProps {
    kind: 'terms' | 'privacy';
}

export default function LegalDocument({ kind }: LegalDocumentProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const lang: Lang = i18n.language?.startsWith('es') ? 'es' : 'en';
    const doc: LegalDoc = kind === 'terms' ? termsContent[lang] : privacyContent[lang];

    const dateLocale = lang === 'es' ? 'es-ES' : 'en-US';
    const formattedDate = new Date(LEGAL_EFFECTIVE_DATE).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const otherKind = kind === 'terms' ? 'privacy' : 'terms';
    const otherPath = kind === 'terms' ? '/privacy' : '/terms';
    const otherLabel = otherKind === 'terms' ? t('legal.terms') : t('legal.privacy');

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Top bar */}
            <header className="sticky top-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                            <Map className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-display font-bold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">
                            {COMPANY_NAME}
                        </span>
                    </Link>

                    {/* Language toggle */}
                    <div className="flex items-center gap-1 text-sm">
                        <button
                            type="button"
                            onClick={() => i18n.changeLanguage('es')}
                            className={`px-2 py-1 rounded-md font-medium transition-colors ${
                                lang === 'es'
                                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            ES
                        </button>
                        <button
                            type="button"
                            onClick={() => i18n.changeLanguage('en')}
                            className={`px-2 py-1 rounded-md font-medium transition-colors ${
                                lang === 'en'
                                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            EN
                        </button>
                    </div>
                </div>
            </header>

            {/* Document */}
            <main className="max-w-3xl mx-auto px-6 py-10">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('common.back')}
                </button>

                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{doc.title}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                    {t('legal.lastUpdated')}: {formattedDate}
                </p>

                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-10">{doc.intro}</p>

                <div className="space-y-10">
                    {doc.sections.map((section, idx) => (
                        <section key={idx}>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                <span className="text-primary-600 mr-2">{idx + 1}.</span>
                                {section.heading}
                            </h2>
                            <div className="space-y-3">
                                {section.blocks.map((block, bIdx) => {
                                    if (block.type === 'p') {
                                        return (
                                            <p
                                                key={bIdx}
                                                className="text-gray-700 dark:text-gray-300 leading-relaxed"
                                            >
                                                {block.text}
                                            </p>
                                        );
                                    }
                                    if (block.type === 'callout') {
                                        return (
                                            <div
                                                key={bIdx}
                                                className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-r-lg p-4 text-sm text-amber-900 dark:text-amber-200 leading-relaxed"
                                            >
                                                {block.text}
                                            </div>
                                        );
                                    }
                                    return (
                                        <ul
                                            key={bIdx}
                                            className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 leading-relaxed"
                                        >
                                            {block.items.map((item, iIdx) => (
                                                <li key={iIdx}>{item}</li>
                                            ))}
                                        </ul>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                </div>

                {/* Footer */}
                <footer className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
                    <p className="text-gray-500 dark:text-gray-400">
                        © {new Date(LEGAL_EFFECTIVE_DATE).getFullYear()} {COMPANY_NAME} · {OPERATOR_NAME} · RUC {OPERATOR_RUC} · {OPERATOR_COUNTRY}
                    </p>
                    <Link to={otherPath} className="text-primary-600 hover:text-primary-700 font-medium">
                        {otherLabel} →
                    </Link>
                </footer>
            </main>
        </div>
    );
}
